import { useEffect, useState } from 'react';
import { Card, DataTable, FilterBar, Modal, FormField, TextInput, Drawer, Tabs, Stat, StatusBadge, EmptyState, PageHeader, Toggle } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { labelFor } from '@/shared/lib/labels';
import { adminApi, type AgentPermissions, type RoleRow } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { useTicker, elapsedSec, fmtDuration } from '@/shared/hooks/useTicker';

/** סיסמה שעומדת במדיניות השרת: 12+ תווים, גדולה+קטנה, ספרה ותו מיוחד. */
function generateStrongPassword(): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%^&*';
  const all = upper + lower + digits + symbols;
  const rand = (pool: string) => pool[Math.floor((crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32) * pool.length)];
  const base = [rand(upper), rand(lower), rand(digits), rand(symbols)];
  while (base.length < 14) base.push(rand(all));
  // ערבוב פשוט כדי שהתבנית לא תהיה קבועה.
  for (let i = base.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
    [base[i], base[j]] = [base[j], base[i]];
  }
  return base.join('');
}

interface Agent {
  id: number;
  name: string;
  email: string;
  role: string;
  extension?: string;
  status: string;
  supervisor_id?: number | null;
  outbound_did_id?: number | null;
  outbound_did_number?: string | null;
  outbound_did_ids?: number[];
  inbound_did_id?: number | null;
  inbound_did_numbers?: string[];
  last_login_at?: string | null;
  preferences?: { mobile_forward?: { enabled?: boolean; number?: string | null } };
}

// זמן התחברות אחרון — יחסי לאחרונה, תאריך מלא לישן, "—" כשריק.
function formatLastLogin(iso?: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 60) return 'הרגע';
  if (diffSec < 3600) return `לפני ${Math.floor(diffSec / 60)} דק׳`;
  if (diffSec < 86400) return `לפני ${Math.floor(diffSec / 3600)} שע׳`;
  if (diffSec < 604800) return `לפני ${Math.floor(diffSec / 86400)} ימים`;
  return new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface DidOption { id: number; number: string; label?: string | null; usage?: string | null }

/**
 * "איפה המספר משמש" — צ'יפ קטן עם אייקון לפי סוג הניתוב (תא קולי / IVR / תור /
 * שלוחה / סוכן קולי). ה-usage מגיע מוכן מהשרת (settings→telephony.dids).
 */
function UsageChip({ usage }: { usage: string }) {
  const icon = usage.startsWith('תא קולי') ? 'voicemail'
    : usage.startsWith('IVR') ? 'keypad'
    : usage.startsWith('תור') ? 'users'
    : usage.startsWith('סוכן קולי') ? 'mic'
    : 'phoneIn';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
      fontSize: 'var(--t-xs)', color: 'var(--accent-ink)', background: 'var(--accent-soft)',
      border: '1px solid var(--accent)', borderRadius: 'var(--r-pill)', padding: '1px 8px',
    }}>
      <Icon name={icon} size={11} color="var(--accent)" />
      משמש כ: {usage}
    </span>
  );
}

/**
 * מפרק שגיאת API לשגיאות פר-שדה: 422 של Laravel מגיע כ-{errors: {field: [msgs]}};
 * abort(422, 'טקסט') מגיע כ-message בלבד — נכנס תחת המפתח הכללי _general.
 */
export function fieldErrorsFrom(e: unknown): Record<string, string> {
  const resp = (e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response;
  const out: Record<string, string> = {};
  for (const [field, msgs] of Object.entries(resp?.data?.errors ?? {})) {
    if (msgs?.length) out[field] = msgs.join(' ');
  }
  if (!Object.keys(out).length && resp?.data?.message) out._general = resp.data.message;
  return out;
}

export function Agents({ embedded = false }: { embedded?: boolean } = {}) {
  const [rows, setRows] = useState<Agent[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState<Agent | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);
  const [drawer, setDrawer] = useState<Agent | null>(null);
  const [tab, setTab] = useState<'details' | 'permissions' | 'hours' | 'stats'>('details');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'agent', extension: '', supervisor_id: '' });
  // מספר ייעודי לנציג חדש (יוצא+נכנס) — משויך אחרי היצירה, כשיש agent_id.
  const [addDedicatedDid, setAddDedicatedDid] = useState('');
  // שגיאות פר-שדה בטופס ההוספה (מפתח _general = שגיאה שאינה של שדה מסוים).
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '', role: 'agent', extension: '', status: 'active', supervisor_id: '', outbound_did_id: '' });
  // העברה לנייד — endpoint נפרד (כמו caller-id), כי זו הגדרה שגורמת
  // למרכזייה לחייג החוצה ויש לה ולידציה משלה.
  const [editMobileFwd, setEditMobileFwd] = useState({ enabled: false, number: '' });
  const [showNewPassword, setShowNewPassword] = useState(false);
  // Full allowed caller-id set for the agent (the softphone picker options).
  const [editDidIds, setEditDidIds] = useState<number[]>([]);
  // Direct inbound number (a DID routed straight to the agent's extension).
  const [editInboundDid, setEditInboundDid] = useState<string>('');
  // Potential managers an agent can report to (supervisor / admin / owner).
  const [managers, setManagers] = useState<Agent[]>([]);
  // תפקידים דינמיים של המרכזיה — מזין את בוררי התפקיד.
  const [roleOptions, setRoleOptions] = useState<RoleRow[]>([]);
  // DID policy + available numbers, loaded once from settings. When per-agent is
  // off, the per-agent number picker is hidden (everyone shares one number).
  const [perAgentDids, setPerAgentDids] = useState(false);
  const [dids, setDids] = useState<DidOption[]>([]);

  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Load the manager pool once (unfiltered by the search box).
  useEffect(() => {
    void adminApi.agents({ per_page: 100 })
      .then((res) => setManagers((res.data as Agent[]).filter((a) => ['supervisor', 'admin', 'owner'].includes(a.role))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    void adminApi.roles()
      .then((res) => setRoleOptions((res.data as RoleRow[]) ?? []))
      .catch(() => {});
  }, []);

  // Load DID policy + numbers for the per-agent caller-id picker.
  useEffect(() => {
    void adminApi.settings()
      .then((d) => {
        const tel = (d as { telephony?: { per_agent_dids?: boolean; dids?: DidOption[] } })?.telephony;
        setPerAgentDids(!!tel?.per_agent_dids);
        setDids(tel?.dids ?? []);
      })
      .catch(() => {});
  }, []);

  async function load() {
    setLoading(true);
    const res = await adminApi.agents({ q });
    setRows(res.data as Agent[]);
    setLoading(false);
  }

  /** שם התפקיד להצגה — מהתפקידים הדינמיים של המרכזיה, ואם אין, מהמילון. */
  function roleLabel(slug: string): string {
    return roleOptions.find((r) => r.slug === slug)?.name ?? labelFor('role', slug);
  }

  /** האם התפקיד (slug) הוא persona של נציג — קובע מפקח/שדות רלוונטיים. */
  function isAgentPersona(slug: string): boolean {
    const role = roleOptions.find((r) => r.slug === slug);
    return (role?.persona ?? (slug === 'agent' ? 'agent' : 'admin')) === 'agent';
  }

  /** עדכון שדה בטופס ההוספה + ניקוי השגיאה שלו ברגע שמתקנים. */
  function setField(key: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    setFormErrors((er) => {
      if (!er[key] && !er._general) return er;
      const rest = { ...er };
      delete rest[key];
      delete rest._general;
      return rest;
    });
  }

  async function create() {
    const missing: Record<string, string> = {};
    if (!form.name) missing.name = 'חובה להזין שם.';
    if (!form.email) missing.email = 'חובה להזין אימייל.';
    if (!form.password) missing.password = 'חובה להזין סיסמה.';
    if (Object.keys(missing).length) {
      setFormErrors(missing);
      return;
    }
    try {
      const created = await adminApi.createAgent({
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        // שלוחה לא נשלחת — מוקצית אוטומטית בשרת (המספר הפנוי הבא).
        // Only agents report to a manager.
        supervisor_id: isAgentPersona(form.role) && form.supervisor_id ? Number(form.supervisor_id) : null,
      });
      // שיוך המספר הייעודי (יוצא+נכנס) לנציג החדש, אם נבחר — דורש agent_id שחזר.
      const newId = (created as { id?: number } | null)?.id;
      if (newId && addDedicatedDid) {
        const didId = Number(addDedicatedDid);
        try {
          if (perAgentDids) await adminApi.setAgentOutboundDid(newId, didId, [didId]);
          await adminApi.setAgentInboundDid(newId, didId);
        } catch {
          toast('הנציג נוסף, אך שיוך המספר נכשל — הגדר אותו בעריכת הנציג.', 'warn');
        }
      }
      toast('נוסף נציג', 'ok');
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: 'agent', extension: '', supervisor_id: '' });
      setAddDedicatedDid('');
      setFormErrors({});
      void load();
    } catch (e) {
      const errors = fieldErrorsFrom(e);
      // הודעות abort() מהשרת מגיעות כ-message כללי — נעגן אותן לשדה הרלוונטי.
      if (errors._general?.includes('מפקח')) {
        errors.supervisor_id = errors._general;
        delete errors._general;
      } else if (errors._general?.includes('תפקיד')) {
        errors.role = errors._general;
        delete errors._general;
      }
      setFormErrors(Object.keys(errors).length ? errors : { _general: 'שגיאה בהוספת נציג. נסה שוב.' });
    }
  }

  function openEdit(a: Agent) {
    setEditForm({ name: a.name, email: a.email, password: '', role: a.role, extension: a.extension ?? '', status: a.status, supervisor_id: a.supervisor_id ? String(a.supervisor_id) : '', outbound_did_id: a.outbound_did_id ? String(a.outbound_did_id) : '' });
    setEditMobileFwd({
      enabled: a.preferences?.mobile_forward?.enabled ?? false,
      number: a.preferences?.mobile_forward?.number ?? '',
    });
    setShowNewPassword(false);
    setEditDidIds(a.outbound_did_ids ?? (a.outbound_did_id ? [a.outbound_did_id] : []));
    setEditInboundDid(a.inbound_did_id ? String(a.inbound_did_id) : '');
    setEditTarget(a);
  }

  /**
   * שיוך מספר ייעודי בלחיצה אחת: המספר הנבחר הופך גם ליוצא (Caller ID ברירת
   * מחדל + נוסף לרשימה המורשית, אם המדיניות פר-נציג) וגם לנכנס הישיר. ריק =
   * ניתוק שני הכיוונים. הכל דרך ה-state הקיים, כך שכפתור "שמור" מתמיד כרגיל.
   */
  function assignDedicatedNumber(didIdStr: string) {
    setEditInboundDid(didIdStr);
    if (!perAgentDids) return;
    if (!didIdStr) {
      // ניקוי: מסירים את הייעודי מרשימת היוצאים ומברירת המחדל.
      setEditDidIds([]);
      setEditForm((f) => ({ ...f, outbound_did_id: '' }));
      return;
    }
    const didId = Number(didIdStr);
    setEditDidIds((prev) => (prev.includes(didId) ? prev : [...prev, didId]));
    setEditForm((f) => ({ ...f, outbound_did_id: didIdStr }));
  }
  // המספר הייעודי = כשאותו מספר הוא גם הנכנס וגם היוצא-ברירת-מחדל.
  const dedicatedDidId = perAgentDids
    ? (editInboundDid && editInboundDid === editForm.outbound_did_id ? editInboundDid : '')
    : editInboundDid;

  async function saveEdit() {
    if (!editTarget) return;
    try {
      await adminApi.updateAgent(editTarget.id, {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        extension: editForm.extension || null,
        status: editForm.status,
        // Clear the manager link when the role isn't an agent-persona role.
        supervisor_id: isAgentPersona(editForm.role) && editForm.supervisor_id ? Number(editForm.supervisor_id) : null,
        // איפוס סיסמה — נשלח רק אם הוזנה סיסמה חדשה.
        ...(editForm.password ? { password: editForm.password } : {}),
      });
      // העברה לנייד — נשלח רק כשהשתנה, כדי לא לדרוס הגדרה שהנציג
      // עצמו שינה מהמסך שלו בין טעינת הטופס לשמירה.
      const prevFwd = editTarget.preferences?.mobile_forward;
      if ((prevFwd?.enabled ?? false) !== editMobileFwd.enabled
        || (prevFwd?.number ?? '') !== editMobileFwd.number) {
        await adminApi.setAgentMobileForward(
          editTarget.id, editMobileFwd.enabled, editMobileFwd.number || null,
        );
      }
      // Per-agent caller-id is a separate endpoint (it lives on the extension).
      // Only persist it when the policy is on, and only when it changed.
      if (perAgentDids) {
        const next = editForm.outbound_did_id ? Number(editForm.outbound_did_id) : null;
        const prevIds = [...(editTarget.outbound_did_ids ?? [])].sort().join(',');
        const nextIds = [...editDidIds].sort().join(',');
        if (next !== (editTarget.outbound_did_id ?? null) || prevIds !== nextIds) {
          await adminApi.setAgentOutboundDid(editTarget.id, next, editDidIds);
        }
      }
      // Direct inbound number: route the chosen DID to this agent's extension.
      const nextInbound = editInboundDid ? Number(editInboundDid) : null;
      if (nextInbound !== (editTarget.inbound_did_id ?? null)) {
        await adminApi.setAgentInboundDid(editTarget.id, nextInbound);
      }
      toast('עודכן', 'ok');
      setEditTarget(null);
      setDrawer(null);
      void load();
    } catch (e) {
      const errors = fieldErrorsFrom(e);
      toast(errors._general ?? Object.values(errors)[0] ?? 'שגיאה בעדכון', 'danger');
    }
  }

  async function remove(a: Agent) {
    try {
      await adminApi.deleteAgent(a.id);
      toast('הנציג הוסר', 'ok');
      setDeleteTarget(null);
      setDrawer((d) => (d && d.id === a.id ? null : d));
      setRows((prev) => prev.filter((r) => r.id !== a.id));
    } catch {
      toast('שגיאה בהסרה', 'danger');
    }
  }

  // מי כבר מחזיק כל מספר — מחושב מרשימת הנציגים הטעונה (ללא קריאת API נוספת).
  // מספר יוצא (Caller ID) יכול להיות משותף לכמה נציגים; מספר ישיר נכנס הוא 1:1.
  // מתעלמים מהנציג הנערך עצמו כדי לא להציג "כבר שייך לך".
  function outboundHolders(didId: number, exceptId?: number): string[] {
    return rows
      .filter((a) => a.id !== exceptId && (a.outbound_did_ids ?? []).includes(didId))
      .map((a) => a.name);
  }
  function inboundHolder(didId: number, exceptId?: number): string | null {
    const a = rows.find((r) => r.id !== exceptId && r.inbound_did_id === didId);
    return a?.name ?? null;
  }

  /**
   * 07/09/2026 (דורון: "שיהיה ברור יותר"): הטבלה קובצה משמונה עמודות שטוחות
   * לארבע קבוצות משמעות — מי הנציג (שם+אימייל+תפקיד), הטלפוניה שלו
   * (שלוחה/מספר יוצא/מספר ישיר), מצבו, ומתי התחבר. פחות עמודות = סריקה מהירה.
   */
  const cols: DataTableColumn<Agent>[] = [
    {
      key: 'name',
      he: 'נציג',
      render: (r) => (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
          <Avatar name={r.name} idx={r.id} size={34} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
            <div dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', textAlign: 'start', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      he: 'תפקיד',
      render: (r) => (
        <div className="stack" style={{ gap: 3, alignItems: 'flex-start' }}>
          <StatusBadge tone={r.role === 'owner' ? 'accent' : r.role === 'agent' ? 'neutral' : 'info'} size="sm">
            {roleLabel(r.role)}
          </StatusBadge>
          {isAgentPersona(r.role) && r.supervisor_id != null && (
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
              מפקח: {managers.find((m) => m.id === r.supervisor_id)?.name ?? '—'}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'extension',
      he: 'טלפוניה',
      render: (r: Agent) => {
        const direct = r.inbound_did_numbers?.length ? r.inbound_did_numbers.join(', ') : null;
        const missingOutbound = perAgentDids && !r.outbound_did_number;
        return (
          <div className="stack" style={{ gap: 3, alignItems: 'flex-start', fontSize: 'var(--t-sm)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Icon name="keypad" size={12} color="var(--muted)" />
              שלוחה <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{r.extension || '—'}</span>
            </span>
            {perAgentDids && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: missingOutbound ? 'var(--danger)' : 'var(--text-2)' }}>
                <Icon name="phoneOut" size={12} color={missingOutbound ? 'var(--danger)' : 'var(--muted)'} />
                {missingOutbound ? 'אין מספר יוצא — חיוג חסום' : <>יוצא <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{r.outbound_did_number}</span></>}
              </span>
            )}
            {direct && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-2)' }}>
                <Icon name="phoneIn" size={12} color="var(--muted)" />
                ישיר <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{direct}</span>
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      he: 'סטטוס',
      render: (r) => (
        <StatusBadge tone={r.status === 'active' ? 'ok' : 'warn'} size="sm" dot>
          {labelFor('user_status', r.status)}
        </StatusBadge>
      ),
    },
    {
      key: 'last_login_at' as const,
      he: 'התחברות אחרונה',
      render: (r: Agent) => (
        <span dir="auto" style={{ fontSize: 'var(--t-sm)', color: r.last_login_at ? 'var(--text-2)' : 'var(--muted)' }}>
          {formatLastLogin(r.last_login_at)}
        </span>
      ),
    },
  ];

  // סיכום מהיר בראש המסך — כמה נציגים, כמה פעילים, וכמה חסרים מספר יוצא
  // (חיוג חסום) או לא התחברו מעולם. אלה שתי התקלות השקטות של המסך הזה.
  const activeCount = rows.filter((r) => r.status === 'active').length;
  const missingDid = perAgentDids ? rows.filter((r) => !r.outbound_did_number).length : 0;
  const neverLoggedIn = rows.filter((r) => !r.last_login_at).length;

  return (
    <div className="stack" style={embedded ? undefined : { padding: 'var(--gap)' }}>
      <PageHeader
        title="נציגים"
        subtitle="משתמשי המרכזיה — פרטי כניסה, תפקיד והרשאות, שלוחה ומספרי טלפון. לחיצה על שורה פותחת את כרטיס הנציג."
        actions={<button type="button" className="btn primary" onClick={() => setShowAdd(true)}><Icon name="plus" size={14} /> הוסף נציג</button>}
      />

      {!loading && rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--gap)' }}>
          <Stat label="נציגים" value={String(rows.length)} hint={`${activeCount} פעילים`} />
          {perAgentDids && (
            <Stat
              label="ללא מספר יוצא"
              value={String(missingDid)}
              hint={missingDid > 0 ? 'חיוג יוצא חסום עבורם' : 'לכולם יש מספר לחיוג'}
            />
          )}
          <Stat label="לא התחברו מעולם" value={String(neverLoggedIn)} hint={neverLoggedIn > 0 ? 'שלח להם את פרטי הכניסה' : 'כולם התחברו לפחות פעם אחת'} />
        </div>
      )}

      <FilterBar search={q} onSearch={setQ} onClear={() => setQ('')} placeholder="חיפוש לפי שם, אימייל או שלוחה" />
      <Card padded={false}>
        <DataTable<Agent> columns={cols} rows={rows} loading={loading} onRowClick={setDrawer} emptyState={<EmptyState icon="users" title={q ? 'לא נמצאו נציגים' : 'אין עדיין נציגים'} description={q ? 'נסה חיפוש אחר, או נקה את החיפוש.' : 'הוסף את הנציג הראשון כדי להתחיל לנהל את הצוות.'} action={q ? undefined : <button type="button" className="btn primary" onClick={() => setShowAdd(true)}><Icon name="plus" size={14} /> הוסף נציג</button>} />} />
      </Card>

      <Modal
        isOpen={showAdd}
        onClose={() => { setShowAdd(false); setFormErrors({}); }}
        title="הוסף נציג"
        footer={
          <>
            <button type="button" className="btn" onClick={() => { setShowAdd(false); setFormErrors({}); }}>ביטול</button>
            <button type="button" className="btn primary" onClick={create}>שמור</button>
          </>
        }
      >
        <div className="stack">
          {formErrors._general && (
            <div role="alert" style={{ padding: '8px 12px', background: 'color-mix(in srgb, var(--danger) 10%, transparent)', border: '1px solid var(--danger)', borderRadius: 'var(--r-2)', color: 'var(--danger)', fontSize: 'var(--t-sm)' }}>
              {formErrors._general}
            </div>
          )}
          <FormField label="שם" required error={formErrors.name}><TextInput value={form.name} onChange={(e) => setField('name', e.target.value)} /></FormField>
          <FormField label="אימייל" required error={formErrors.email}><TextInput type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} dir="ltr" /></FormField>
          <FormField label="סיסמה" required error={formErrors.password} hint="לפחות 12 תווים, עם אות גדולה וקטנה באנגלית, ספרה ותו מיוחד (למשל ! או #).">
            <TextInput type="password" value={form.password} onChange={(e) => setField('password', e.target.value)} />
          </FormField>
          <FormField label="תפקיד" error={formErrors.role} hint="תפקידים מנוהלים במסך ״תפקידים״ — כולל אילו מסכים והרשאות יש לכל תפקיד.">
            <select value={form.role} onChange={(e) => setField('role', e.target.value)} style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
              {roleSelectOptions(roleOptions)}
            </select>
          </FormField>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            השלוחה תוקצה אוטומטית — המספר הפנוי הבא במרכזיה.
          </div>
          {isAgentPersona(form.role) && (
            <FormField label="מפקח" error={formErrors.supervisor_id} hint="המפקח יוכל לראות את שיחות והקלטות הנציג תחת ״הצוות שלי״">
              <select value={form.supervisor_id} onChange={(e) => setField('supervisor_id', e.target.value)} style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
                <option value="">— ללא מפקח —</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} ({labelFor('role', m.role)})</option>
                ))}
              </select>
            </FormField>
          )}

          {/* מספר ייעודי לנציג החדש — יוצא+נכנס בבחירה אחת (משויך אחרי היצירה). */}
          {dids.length > 0 && (
            <div style={{ border: '2px solid var(--accent)', borderRadius: 'var(--r-3, 12px)', background: 'var(--accent-soft, var(--surface-2))', padding: 'var(--pad-card)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Icon name="phone" size={18} color="var(--accent)" />
                <strong style={{ fontSize: 'var(--t-lg)' }}>מספר ייעודי לנציג</strong>
              </div>
              <p style={{ margin: '0 0 10px', fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
                {perAgentDids
                  ? 'בחר מספר שישויך לנציג — גם כמספר יוצא (Caller ID) וגם כמספר ישיר לשיחות נכנסות. אפשר גם להשאיר ריק ולהגדיר בעריכה.'
                  : 'בחר מספר שינותב ישירות לנציג לשיחות נכנסות. (מדיניות מספר יוצא משותפת פעילה).'}
              </p>
              <select
                value={addDedicatedDid}
                onChange={(e) => setAddDedicatedDid(e.target.value)}
                style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
              >
                <option value="">— ללא מספר ייעודי —</option>
                {dids.map((d) => {
                  const holder = inboundHolder(d.id);
                  const base = d.label ? `${d.number} — ${d.label}` : d.number;
                  const use = d.usage ? ` [${d.usage}]` : '';
                  return (
                    <option key={d.id} value={d.id}>
                      {base}{use}{holder ? ` — כרגע אצל ${holder}` : ' — פנוי'}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      </Modal>

      {editTarget && (
        <Modal
          isOpen
          onClose={() => setEditTarget(null)}
          title={`עריכת ${editTarget.name}`}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setEditTarget(null)}>ביטול</button>
              <button type="button" className="btn primary" onClick={saveEdit}>שמור</button>
            </>
          }
        >
          <div className="stack">
            <FormField label="שם" required><TextInput value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></FormField>
            <FormField label="אימייל" required>
              <TextInput type="email" dir="ltr" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </FormField>
            <FormField label="איפוס סיסמה" hint="השאר ריק כדי לשמור על הסיסמה הקיימת. לפחות 12 תווים, אותיות גדולות/קטנות, ספרה ותו מיוחד.">
              <div style={{ display: 'flex', gap: 6 }}>
                <TextInput
                  type={showNewPassword ? 'text' : 'password'}
                  dir="ltr"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="סיסמה חדשה (אופציונלי)"
                  autoComplete="new-password"
                  style={{ flex: 1 }}
                />
                <button type="button" className="btn ghost" title={showNewPassword ? 'הסתר' : 'הצג'} onClick={() => setShowNewPassword((v) => !v)}>
                  <Icon name={showNewPassword ? 'eyeOff' : 'eye'} size={15} />
                </button>
                <button
                  type="button"
                  className="btn"
                  title="יצירת סיסמה חזקה"
                  onClick={() => { setEditForm((f) => ({ ...f, password: generateStrongPassword() })); setShowNewPassword(true); }}
                >
                  צור סיסמה
                </button>
                {editForm.password && (
                  <button type="button" className="btn ghost" title="העתק" onClick={() => { void navigator.clipboard.writeText(editForm.password).then(() => toast('הועתק', 'ok')).catch(() => {}); }}>
                    <Icon name="copy" size={15} />
                  </button>
                )}
              </div>
            </FormField>
            <FormField label="תפקיד">
              <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
                {roleSelectOptions(roleOptions, editForm.role)}
              </select>
            </FormField>
            {isAgentPersona(editForm.role) && (
              <FormField label="מפקח" hint="המפקח יוכל לראות את שיחות והקלטות הנציג תחת ״הצוות שלי״">
                <select value={editForm.supervisor_id} onChange={(e) => setEditForm({ ...editForm, supervisor_id: e.target.value })} style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
                  <option value="">— ללא מפקח —</option>
                  {managers.filter((m) => m.id !== editTarget.id).map((m) => (
                    <option key={m.id} value={m.id}>{m.name} ({labelFor('role', m.role)})</option>
                  ))}
                </select>
              </FormField>
            )}
            <FormField label="סטטוס">
              <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
                <option value="active">פעיל</option>
                <option value="suspended">מושעה</option>
              </select>
            </FormField>
            {/* שיוך מהיר: מספר ייעודי לנציג — גם יוצא וגם נכנס בבת אחת. */}
            {dids.length > 0 && (
              <div style={{ border: '2px solid var(--accent)', borderRadius: 'var(--r-3, 12px)', background: 'var(--accent-soft, var(--surface-2))', padding: 'var(--pad-card)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Icon name="phone" size={18} color="var(--accent)" />
                  <strong style={{ fontSize: 'var(--t-lg)' }}>מספר ייעודי לנציג</strong>
                </div>
                <p style={{ margin: '0 0 10px', fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
                  {perAgentDids
                    ? 'בחירת מספר כאן משייכת אותו לנציג בלחיצה אחת — גם כמספר יוצא (Caller ID ברירת מחדל) וגם כמספר ישיר לשיחות נכנסות.'
                    : 'בחירת מספר כאן מנתבת שיחות נכנסות אליו ישירות לנציג. (מדיניות מספר יוצא משותפת פעילה — Caller ID לא משתנה פר-נציג).'}
                </p>
                <select
                  value={dedicatedDidId}
                  onChange={(e) => assignDedicatedNumber(e.target.value)}
                  style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
                >
                  <option value="">— ללא מספר ייעודי —</option>
                  {dids.map((d) => {
                    const holder = inboundHolder(d.id, editTarget.id);
                    const base = d.label ? `${d.number} — ${d.label}` : d.number;
                    const use = d.usage ? ` [${d.usage}]` : '';
                    return (
                      <option key={d.id} value={d.id}>
                        {base}{use}{holder ? ` — כרגע אצל ${holder}` : ' — פנוי'}
                      </option>
                    );
                  })}
                </select>
                {dedicatedDidId && (
                  <div style={{ marginTop: 8, fontSize: 'var(--t-xs)', color: 'var(--accent-ink)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Icon name="check" size={12} color="var(--ok)" />
                    {perAgentDids ? 'המספר משויך גם ליוצא וגם לנכנס.' : 'המספר משויך כמספר נכנס ישיר.'}
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                  להגדרה ידנית נפרדת (מספרים יוצאים מרובים / נכנס שונה מיוצא) — השתמש בשדות שמתחת.
                </div>
              </div>
            )}
            {perAgentDids && (
              <FormField label="מספרים יוצאים (Caller ID)" hint="סמן את כל המספרים שהנציג רשאי לחייג מהם — הם יופיעו בבורר בסופטפון. בחר גם מספר ברירת מחדל. ללא מספרים — חיוג יוצא ייחסם.">
                {dids.length === 0 ? (
                  <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>אין מספרים מוגדרים. הוסף מספר במסך "מספרים" (DIDs).</div>
                ) : (
                  <div className="stack" style={{ gap: 6 }}>
                    {dids.map((d) => {
                      const checked = editDidIds.includes(d.id);
                      const isPrimary = editForm.outbound_did_id === String(d.id);
                      const holders = outboundHolders(d.id, editTarget.id);
                      return (
                        <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: checked ? 'var(--surface-2)' : 'var(--surface)' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked ? [...editDidIds, d.id] : editDidIds.filter((x) => x !== d.id);
                              setEditDidIds(next);
                              // Keep the primary valid: auto-pick the first allowed, clear when none.
                              if (!e.target.checked && isPrimary) {
                                setEditForm({ ...editForm, outbound_did_id: next.length ? String(next[0]) : '' });
                              } else if (e.target.checked && !editForm.outbound_did_id) {
                                setEditForm({ ...editForm, outbound_did_id: String(d.id) });
                              }
                            }}
                          />
                          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)', textAlign: 'start' }}>
                              {d.label ? `${d.number} — ${d.label}` : d.number}
                            </span>
                            {d.usage && <UsageChip usage={d.usage} />}
                            {holders.length > 0
                              ? <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>גם אצל: {holders.join(', ')}</span>
                              : <span style={{ fontSize: 'var(--t-xs)', color: 'var(--ok)' }}>לא בשימוש אצל נציג אחר</span>}
                          </span>
                          {checked && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--t-xs)', color: isPrimary ? 'var(--accent-ink)' : 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <input
                                type="radio"
                                name="primary-did"
                                checked={isPrimary}
                                onChange={() => setEditForm({ ...editForm, outbound_did_id: String(d.id) })}
                              />
                              ברירת מחדל
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </FormField>
            )}
            <FormField
              label="העברה לנייד"
              hint="כשמתקשרים לשלוחה, הנייד יצלצל במקביל. השיחה תוקלט ותתומלל כרגיל."
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <TextInput
                  value={editMobileFwd.number}
                  onChange={(e) => setEditMobileFwd((s) => ({ ...s, number: e.target.value }))}
                  dir="ltr"
                  inputMode="tel"
                  placeholder="050-000-0000"
                />
                <Toggle
                  checked={editMobileFwd.enabled}
                  onChange={(next) => setEditMobileFwd((s) => ({ ...s, enabled: next }))}
                  label="פעיל"
                />
              </div>
            </FormField>
            <FormField label="מספר ישיר לשיחות נכנסות" hint="לקוח שמחייג למספר הזה יגיע ישירות לנציג (מנתב את המספר לשלוחה שלו). מספר שכבר משויך לנציג אחר יעבור לנציג הזה אם תבחר בו.">
              <select value={editInboundDid} onChange={(e) => setEditInboundDid(e.target.value)} style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
                <option value="">— ללא מספר ישיר —</option>
                {dids.map((d) => {
                  const holder = inboundHolder(d.id, editTarget.id);
                  const base = d.label ? `${d.number} — ${d.label}` : d.number;
                  const use = d.usage ? ` [${d.usage}]` : '';
                  return (
                    <option key={d.id} value={d.id}>
                      {base}{use}{holder ? ` — משויך ל${holder}` : ' — פנוי'}
                    </option>
                  );
                })}
              </select>
            </FormField>
            {/* בחירה מהירה בלחיצה: כל מספר הוא כפתור. לחיצה על הנבחר מבטלת. ירוק=פנוי, כתום=תפוס אצל נציג אחר. */}
            {dids.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {dids.map((d) => {
                  const holder = inboundHolder(d.id, editTarget.id);
                  const isMine = editInboundDid === String(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setEditInboundDid(isMine ? '' : String(d.id))}
                      title={isMine ? 'לחץ כדי לבטל' : holder ? `כרגע אצל ${holder} — לחץ כדי להעביר לנציג הזה` : 'לחץ כדי לשייך'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-xs)',
                        padding: '4px 10px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                        border: `1px solid ${isMine ? 'var(--accent)' : 'var(--line)'}`,
                        background: isMine ? 'var(--accent-soft)' : 'var(--surface)',
                        color: isMine ? 'var(--accent-ink)' : holder ? 'var(--muted)' : 'var(--text-2)',
                        fontFamily: 'inherit',
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: isMine ? 'var(--accent)' : holder ? 'var(--warn)' : 'var(--ok)' }} />
                      <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{d.number}</span>
                      <span>{isMine ? '· נבחר' : holder ? `· ${holder}` : '· פנוי'}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          isOpen
          onClose={() => setDeleteTarget(null)}
          title="הסרת נציג"
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button>
              <button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>הסר</button>
            </>
          }
        >
          <p>להסיר את הנציג <strong>{deleteTarget.name}</strong>? הפעולה תבטל את הגישה שלו למערכת.</p>
        </Modal>
      )}

      <Drawer isOpen={!!drawer} onClose={() => setDrawer(null)} title={drawer?.name}>
        {/* כרטיס הנציג: זהות בראש, ואז פרטים בשורות תווית/ערך במקום bold inline. */}
        {drawer && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'var(--pad-card)', borderBottom: '1px solid var(--line)' }}>
            <Avatar name={drawer.name} idx={drawer.id} size={44} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 'var(--t-lg)' }}>{drawer.name}</div>
              <div dir="ltr" style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', textAlign: 'start' }}>{drawer.email}</div>
            </div>
            <StatusBadge tone={drawer.status === 'active' ? 'ok' : 'warn'} size="sm" dot>
              {labelFor('user_status', drawer.status)}
            </StatusBadge>
          </div>
        )}
        <Tabs
          items={[
            { id: 'details', label: 'פרטים' },
            { id: 'permissions', label: 'הרשאות' },
            { id: 'hours', label: 'שעות' },
            { id: 'stats', label: 'סטטיסטיקה' },
          ]}
          value={tab}
          onChange={(id) => setTab(id as never)}
        />
        <div style={{ padding: 'var(--pad-card)' }}>
          {tab === 'details' && drawer && (
            <div className="stack" style={{ gap: 'var(--gap)' }}>
              <DetailGroup title="חשבון">
                <DetailRow label="אימייל" value={<span dir="ltr">{drawer.email}</span>} />
                <DetailRow label="תפקיד" value={roleLabel(drawer.role)} hint="מה הוא רואה ומה מותר לו — נקבע בלשונית ״תפקידים״." />
                {isAgentPersona(drawer.role) && (
                  <DetailRow
                    label="מפקח"
                    value={managers.find((m) => m.id === drawer.supervisor_id)?.name ?? '—'}
                    hint="המפקח רואה את שיחותיו והקלטותיו תחת ״הצוות שלי״."
                  />
                )}
                <DetailRow label="התחברות אחרונה" value={formatLastLogin(drawer.last_login_at)} />
              </DetailGroup>

              <DetailGroup title="טלפוניה">
                <DetailRow label="שלוחה" value={<span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{drawer.extension || '—'}</span>} hint="המספר הפנימי שאליו מחייגים בתוך המרכזיה." />
                {perAgentDids && (
                  <DetailRow
                    label="מספר יוצא"
                    value={drawer.outbound_did_number
                      ? <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{drawer.outbound_did_number}</span>
                      : <span style={{ color: 'var(--danger)' }}>לא הוקצה — חיוג יוצא חסום</span>}
                    hint="המספר שהלקוח רואה כשהנציג מחייג אליו."
                  />
                )}
                <DetailRow
                  label="מספר ישיר"
                  value={drawer.inbound_did_numbers?.length
                    ? <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{drawer.inbound_did_numbers.join(', ')}</span>
                    : '—'}
                  hint="לקוח שמחייג למספר הזה מגיע ישירות לנציג."
                />
                <DetailRow
                  label="העברה לנייד"
                  value={drawer.preferences?.mobile_forward?.enabled
                    ? <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{drawer.preferences.mobile_forward.number ?? '—'}</span>
                    : 'כבויה'}
                  hint="כשמתקשרים לשלוחה, הנייד מצלצל במקביל."
                />
              </DetailGroup>

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn primary" onClick={() => openEdit(drawer)}><Icon name="edit" size={14} /> ערוך נציג</button>
                <button type="button" className="btn danger" onClick={() => setDeleteTarget(drawer)}><Icon name="trash" size={14} /> הסר</button>
              </div>
            </div>
          )}
          {tab === 'permissions' && drawer && <AgentPermissionsTab agentId={drawer.id} />}
          {tab === 'hours' && drawer && <AgentHoursTab agentId={drawer.id} />}
          {tab === 'stats' && drawer && <AgentStatsTab agentId={drawer.id} />}
        </div>
      </Drawer>
    </div>
  );
}

/** קבוצת פרטים בכרטיס הנציג — כותרת קטנה ומעליה שורות תווית/ערך. */
function DetailGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.04em', marginBottom: 6 }}>{title}</div>
      <style>{'.ag-detail-row + .ag-detail-row{border-top:1px solid var(--line)}'}</style>
      <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>{children}</div>
    </section>
  );
}

/**
 * שורת פרט: תווית מימין, ערך משמאל, והסבר קצר מתחת כשהשדה לא מובן מאליו
 * (מה זה שלוחה, מה זה מספר יוצא) — הבקשה של דורון "שיהיה ברור יותר".
 */
function DetailRow({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="ag-detail-row" style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{label}</span>
        <span style={{ fontSize: 'var(--t-md)', fontWeight: 500, textAlign: 'end', minWidth: 0 }}>{value}</span>
      </div>
      {hint && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--faint)' }}>{hint}</span>}
    </div>
  );
}

/** בורר תפקידים דינמי; fallback לתפקידי המערכת אם הרשימה טרם נטענה. */
const FALLBACK_ROLES: RoleRow[] = [
  { id: 0, slug: 'owner', name: 'בעלים', persona: 'admin' },
  { id: 0, slug: 'agent', name: 'נציג', persona: 'agent' },
];

function roleSelectOptions(roles: RoleRow[], current?: string) {
  const base = roles.length ? roles : FALLBACK_ROLES;
  const list = current && !base.some((r) => r.slug === current)
    ? [...base, { id: 0, slug: current, name: current, persona: 'admin' as const }]
    : base;
  return list.map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>);
}

/**
 * טאב הרשאות פר-משתמש: מטריצת מסכים×צפייה/עריכה. הבסיס מגיע מהתפקיד;
 * סימון/ביטול יוצר override (grant/revoke) ספציפי למשתמש הזה בלבד.
 */
function AgentPermissionsTab({ agentId }: { agentId: number }) {
  const [data, setData] = useState<AgentPermissions | null>(null);
  const [effective, setEffective] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setData(null);
    void adminApi.agentPermissions(agentId)
      .then((d) => { setData(d); setEffective(new Set(d.effective)); setDirty(false); })
      .catch(() => toast('שגיאה בטעינת הרשאות', 'danger'));
  }, [agentId]);

  if (!data) return <div style={{ color: 'var(--muted)' }}>טוען…</div>;

  if (data.is_owner) {
    return <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>לבעלים יש תמיד את כל ההרשאות.</p>;
  }

  const catalog = data.catalog ?? {};
  const baseHas = (key: string) => {
    const [screen, action] = key.split('.');
    const actions = data.base?.[screen] ?? [];
    return actions.includes(action) || (action === 'view' && actions.includes('edit'));
  };

  function toggle(key: string, checked: boolean) {
    const [screen, action] = key.split('.');
    setEffective((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
        if (action !== 'view') next.add(`${screen}.view`); // כל פעולה גוררת צפייה
      } else {
        next.delete(key);
        // שלילת צפייה שוללת את כל המסך, כולל הדגלים האטומיים.
        if (action === 'view') {
          for (const k of [...next]) if (k.startsWith(`${screen}.`)) next.delete(k);
        }
      }
      return next;
    });
    setDirty(true);
  }

  async function save() {
    if (!data) return;
    // חישוב overrides: מה שסומן מעבר לבסיס = grant; מה שבוטל מהבסיס = revoke.
    const grant: string[] = [];
    const revoke: string[] = [];
    for (const screen of Object.keys(catalog)) {
      // כולל את הדגלים האטומיים — בלעדיהם הלולאה לא הייתה מייצרת עבורם
      // grant/revoke, והחריגה נשמרת חלקית בשקט.
      const screenActions = [
        ...(data.actions ?? ['view', 'edit']),
        ...Object.keys(data.screen_actions?.[screen] ?? {}),
      ];
      for (const action of screenActions) {
        const key = `${screen}.${action}`;
        const has = effective.has(key);
        if (has && !baseHas(key)) grant.push(key);
        if (!has && baseHas(key)) revoke.push(key);
      }
    }
    setSaving(true);
    try {
      const res = await adminApi.updateAgentPermissions(agentId, { grant, revoke });
      setEffective(new Set(res.effective));
      setDirty(false);
      toast('ההרשאות נשמרו', 'ok');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בשמירת ההרשאות', 'danger');
    } finally {
      setSaving(false);
    }
  }

  // סיכום מצב: כמה חריגות פר-משתמש יש כרגע (מעבר לתפקיד), וכמה מסכים פתוחים.
  const allKeys = Object.keys(catalog).flatMap((screen) => [
    ...(data.actions ?? ['view', 'edit']),
    ...Object.keys(data.screen_actions?.[screen] ?? {}),
  ].map((a) => `${screen}.${a}`));
  const overrideCount = allKeys.filter((k) => effective.has(k) !== baseHas(k)).length;
  const visibleScreens = Object.keys(catalog).filter((sc) => effective.has(`${sc}.view`)).length;

  // קיבוץ המסכים (מהשרת) — ורק המסכים שבאמת בקטלוג של ה-persona הזה.
  const groups: [string, string[]][] = Object.entries(data.groups ?? {})
    .map(([g, screens]) => [g, screens.filter((sc) => sc in catalog)] as [string, string[]])
    .filter(([, screens]) => screens.length > 0);
  const grouped = groups.length > 0
    ? groups
    : [['הרשאות', Object.keys(catalog)] as [string, string[]]];

  function setScreen(screen: string, on: boolean) {
    const actions = [
      ...(data?.actions ?? ['view', 'edit']),
      ...Object.keys(data?.screen_actions?.[screen] ?? {}),
    ];
    setEffective((prev) => {
      const next = new Set(prev);
      for (const a of actions) {
        const key = `${screen}.${a}`;
        // "פתח הכל" נותן צפייה+עריכה; דגלים רגישים (פיקוח וכו') נשארים ידניים.
        if (on ? a === 'view' || a === 'edit' : true) {
          on ? next.add(key) : next.delete(key);
        }
      }
      return next;
    });
    setDirty(true);
  }

  function resetToRole() {
    const base = new Set<string>();
    for (const key of allKeys) if (baseHas(key)) base.add(key);
    setEffective(base);
    setDirty(true);
  }

  return (
    <div className="stack" style={{ gap: 'var(--gap)' }}>
      {/* הסבר + מצב נוכחי. קודם היה משפט אחד ומיד 27 שורות תיבות. */}
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: '10px 12px', fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
        <div>
          הבסיס מגיע מהתפקיד <strong>{data.role?.name}</strong>. סימון או ביטול כאן חלים על המשתמש הזה בלבד.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <StatusBadge tone="neutral" size="sm">{visibleScreens} מסכים פתוחים</StatusBadge>
          {overrideCount > 0
            ? <StatusBadge tone="warn" size="sm" dot>{overrideCount} חריגות מהתפקיד</StatusBadge>
            : <StatusBadge tone="ok" size="sm" dot>זהה לתפקיד</StatusBadge>}
          {overrideCount > 0 && (
            <button type="button" className="btn ghost" style={{ fontSize: 'var(--t-xs)', padding: '2px 8px' }} onClick={resetToRole}>
              החזר להרשאות התפקיד
            </button>
          )}
        </div>
      </div>

      {grouped.map(([group, screens]) => (
        <section key={group}>
          <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.04em', marginBottom: 6 }}>{group}</div>
          <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>
            {screens.map((screen, i) => {
              const label = catalog[screen];
              const atomic = data!.screen_actions?.[screen] ?? {};
              const canView = effective.has(`${screen}.view`);
              const box = (action: string, actionLabel: string) => {
                const key = `${screen}.${action}`;
                const overridden = effective.has(key) !== baseHas(key);
                return (
                  <label
                    key={key}
                    title={overridden ? 'שונה מהתפקיד' : undefined}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                      fontSize: 'var(--t-sm)', whiteSpace: 'nowrap',
                      color: overridden ? 'var(--warn)' : 'var(--text-2)',
                      fontWeight: overridden ? 600 : 400,
                    }}
                  >
                    <input
                      type="checkbox"
                      aria-label={`${label} — ${actionLabel}`}
                      checked={effective.has(key)}
                      onChange={(e) => toggle(key, e.target.checked)}
                    />
                    {actionLabel}
                  </label>
                );
              };
              return (
                <div
                  key={screen}
                  style={{
                    padding: '10px 12px',
                    borderTop: i === 0 ? undefined : '1px solid var(--line)',
                    background: canView ? undefined : 'var(--surface-2)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 'var(--t-md)', fontWeight: 500, color: canView ? 'var(--text)' : 'var(--muted)' }}>
                      {label}
                      {!canView && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 400 }}> · אין גישה</span>}
                    </span>
                    <span style={{ display: 'inline-flex', gap: 14 }}>
                      {box('view', 'צפייה')}
                      {box('edit', 'עריכה')}
                    </span>
                  </div>
                  {/* הרשאות רגישות פר-מסך (האזנה, לחישה, ייצוא…) — מוצגות רק
                      כשיש גישה למסך, אחרת הן חסרות משמעות ורק מרעישות. */}
                  {canView && Object.keys(atomic).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--line)' }}>
                      {Object.entries(atomic)
                        .filter(([action]) => !['view', 'edit'].includes(action))
                        .map(([action, actionLabel]) => box(action, actionLabel))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, position: 'sticky', bottom: 0, background: 'var(--surface)', paddingTop: 8 }}>
        <button type="button" className="btn primary" onClick={save} disabled={saving || !dirty}>
          {saving ? 'שומר…' : dirty ? 'שמור הרשאות' : 'אין שינויים'}
        </button>
      </div>
    </div>
  );
}

const DAYS_HE = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

interface HourRow { day_of_week: number; open: string | null; close: string | null; enabled: boolean }

function AgentHoursTab({ agentId }: { agentId: number }) {
  const [rows, setRows] = useState<HourRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setLoading(true);
    void adminApi
      .agentHours(agentId)
      .then((res) => setRows(normalizeHours(res as HourRow[])))
      .catch(() => setRows(normalizeHours([])))
      .finally(() => {
        setLoading(false);
        setDirty(false);
      });
  }, [agentId]);

  function patch(dow: number, change: Partial<HourRow>) {
    setRows((rs) => rs.map((r) => (r.day_of_week === dow ? { ...r, ...change } : r)));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      const saved = await adminApi.saveAgentHours(agentId, rows);
      setRows(normalizeHours(saved as HourRow[]));
      setDirty(false);
      toast('שעות הנציג נשמרו', 'ok');
    } catch {
      toast('שמירת השעות נכשלה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={{ color: 'var(--muted)' }}>טוען…</div>;

  return (
    <div className="stack">
      <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>
        שעות הזמינות של הנציג. שעות חורגות יסומנו כ"שעות נוספות" ויחושבו בנפרד בדוחות.
      </p>
      <div className="stack" style={{ gap: 4 }}>
        {rows.map((r) => (
          <div
            key={r.day_of_week}
            style={{ display: 'grid', gridTemplateColumns: '24px 70px 1fr', gap: 10, padding: 'var(--pad-row) var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', alignItems: 'center' }}
          >
            <input type="checkbox" checked={r.enabled} onChange={(e) => patch(r.day_of_week, { enabled: e.target.checked })} aria-label={`${DAYS_HE[r.day_of_week]} פעיל`} />
            <strong style={{ fontSize: 'var(--t-sm)', color: r.enabled ? 'var(--text)' : 'var(--muted)' }}>{DAYS_HE[r.day_of_week]}</strong>
            {r.enabled ? (
              <div dir="ltr" style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--t-sm)' }}>
                <input type="time" className="input" value={r.open ?? '08:00'} onChange={(e) => patch(r.day_of_week, { open: e.target.value })} style={{ width: 110 }} />
                <span style={{ color: 'var(--muted)' }}>–</span>
                <input type="time" className="input" value={r.close ?? '17:00'} onChange={(e) => patch(r.day_of_week, { close: e.target.value })} style={{ width: 110 }} />
              </div>
            ) : (
              <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>לא פעיל</span>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn primary" onClick={save} disabled={saving || !dirty}>
          {saving ? 'שומר…' : 'שמור שעות'}
        </button>
      </div>
    </div>
  );
}

/** Ensure a full Sun–Sat week with sensible defaults for missing days. */
function normalizeHours(rows: HourRow[]): HourRow[] {
  const byDay = new Map(rows.map((r) => [r.day_of_week, r]));
  return Array.from({ length: 7 }, (_, dow) => {
    const r = byDay.get(dow);
    return {
      day_of_week: dow,
      open: r?.open ?? '08:00',
      close: r?.close ?? '17:00',
      enabled: r?.enabled ?? false,
    };
  });
}

interface AgentStats {
  period: { from: string; to: string };
  kpis: { calls: number; answered: number; missed: number; aht_sec: number; total_talk_sec: number; answer_rate: number | null };
  dispositions: { label: string; count: number }[];
  leads: { total: number; converted: number; conversion_rate: number | null };
  qa: { count: number; avg: number | null };
  presence: { status: string; since: string | null };
  trend: { label: string; calls: number; answered: number }[];
  today: { calls: number; answered: number; missed: number; aht_sec: number; on_call: boolean; current_call_started_at: string | null };
}

type StatRange = 'today' | 'week' | 'month' | 'custom';
function statRangeDates(r: StatRange, cf: string, ct: string): { from: string; to: string } {
  const t = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  if (r === 'today') return { from: iso(t), to: iso(t) };
  if (r === 'week') { const f = new Date(t); f.setDate(f.getDate() - 6); return { from: iso(f), to: iso(t) }; }
  if (r === 'month') { const f = new Date(t); f.setDate(f.getDate() - 29); return { from: iso(f), to: iso(t) }; }
  return { from: cf || iso(t), to: ct || iso(t) };
}

function AgentStatsTab({ agentId }: { agentId: number }) {
  const [data, setData] = useState<AgentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<StatRange>('month');
  const [cf, setCf] = useState('');
  const [ct, setCt] = useState('');
  const now = useTicker(1000);

  useEffect(() => {
    const { from, to } = statRangeDates(range, cf, ct);
    setLoading(true);
    void adminApi
      .agentStats(agentId, from, to)
      .then((res) => setData(res as AgentStats))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [agentId, range, cf, ct]);

  if (loading && !data) return <div style={{ color: 'var(--muted)' }}>טוען…</div>;
  if (!data) return <div style={{ color: 'var(--muted)' }}>אין נתוני ביצועים.</div>;

  const k = data.kpis;
  const maxTrend = Math.max(1, ...data.trend.map((d) => d.calls));

  return (
    <div className="stack" style={{ gap: 12 }}>
      {/* range picker */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {([['today', 'היום'], ['week', 'שבוע'], ['month', '30 ימים'], ['custom', 'מותאם']] as [StatRange, string][]).map(([v, l]) => (
          <button key={v} type="button" className={`btn ${range === v ? 'primary' : 'ghost'}`} style={{ padding: '3px 9px', fontSize: 'var(--t-xs)' }} onClick={() => setRange(v)}>{l}</button>
        ))}
        {range === 'custom' && (
          <>
            <input type="date" className="input" value={cf} onChange={(e) => setCf(e.target.value)} />
            <input type="date" className="input" value={ct} onChange={(e) => setCt(e.target.value)} />
          </>
        )}
      </div>

      {/* today live strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)' }}>
        <span>היום: <strong>{data.today.calls}</strong> שיחות · {data.today.answered} נענו</span>
        {data.today.on_call
          ? <StatusBadge tone="info" dot size="sm">בשיחה · {fmtDuration(elapsedSec(data.today.current_call_started_at, now))}</StatusBadge>
          : <StatusBadge tone={data.presence.status === 'available' ? 'ok' : 'neutral'} dot size="sm">{presenceLabel(data.presence.status)}</StatusBadge>}
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
        <Stat label="שיחות" value={k.calls} />
        <Stat label="נענו" value={k.answered} hint={k.answer_rate != null ? `${k.answer_rate}%` : undefined} />
        <Stat label="הוחמצו" value={k.missed} />
        <Stat label="AHT ממוצע" value={fmtDuration(k.aht_sec)} />
        <Stat label="סה״כ זמן שיחה" value={fmtDuration(k.total_talk_sec)} />
        <Stat label="ציון QA" value={data.qa.avg != null ? `${data.qa.avg}` : '—'} hint={data.qa.count ? `${data.qa.count} הערכות` : undefined} />
      </div>

      {/* leads / conversion */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
        <Stat label="לידים בקמפיינים" value={data.leads.total} />
        <Stat label="המרות" value={data.leads.converted} />
        <Stat label="אחוז המרה" value={data.leads.conversion_rate != null ? `${data.leads.conversion_rate}%` : '—'} />
      </div>

      {/* 7-day trend sparkline */}
      <Card title="מגמת שיחות (7 ימים)">
        {data.trend.every((d) => d.calls === 0) ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין שיחות בשבוע האחרון.</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 80 }}>
            {data.trend.map((d) => (
              <div key={d.label} style={{ flex: 1, textAlign: 'center' }} title={`${d.calls} שיחות, ${d.answered} נענו`}>
                <div style={{ height: `${(d.calls / maxTrend) * 56}px`, background: 'var(--accent)', borderRadius: 3, minHeight: d.calls ? 3 : 0 }} />
                <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 4 }}>{d.label}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* dispositions */}
      {data.dispositions.length > 0 && (
        <Card title="פילוח תוצאות שיחה">
          {data.dispositions.map((d) => (
            <div key={d.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--line)', fontSize: 'var(--t-sm)' }}>
              <span>{d.label}</span>
              <strong style={{ color: 'var(--accent)' }}>{d.count}</strong>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function presenceLabel(s: string): string {
  return ({ available: 'זמין', oncall: 'בשיחה', acw: 'אחרי שיחה', break: 'הפסקה', training: 'הכשרה', offline: 'מנותק' } as Record<string, string>)[s] ?? s;
}
